if (!customElements.get('custom-size-table')) {

class CustomSizeTable extends HTMLElement {
  constructor() {
    super();
    this.handleCategoryChange = this.handleCategoryChange.bind(this);
    this.handleSubtabClick = this.handleSubtabClick.bind(this);
    this.handleSubtabKeydown = this.handleSubtabKeydown.bind(this);
    /** @type {Array<() => void>} */
    this.regionSelectCleanups = [];
  }

  connectedCallback() {
    if (this.initialized) return;

    this.guidesContainer = this.querySelector('[data-size-guides-container]');
    if (!this.guidesContainer) return;

    this.guides = [...this.guidesContainer.querySelectorAll('[data-guide-key]')];
    this.isMultiGuide = this.dataset.multiGuide === 'true';
    this.multiMode = this.dataset.multiMode || 'carousel';
    this.isDesignMode = window.Shopify?.designMode === true;
    this.setActiveCategory(this.dataset.activeCategory || '');

    this.showAs = this.dataset.showAs;

    document.addEventListener('size-guide:category-change', this.handleCategoryChange);

    this.initialized = true;

    if (this.isDesignMode || this.showAs === 'standalone') {
      this.showAllGuides();
    } else if (this.isMultiGuide) {
      this.applyCategoryVisibility();
      if (this.multiMode === 'tabs') {
        this.setupSubtabs();
      }
    } else {
      this.ensureRegionSelectors();
      this.guides.forEach((guide) => {
        this.showGuideRegion(guide, this.getGuideDefaultRegion(guide));
      });
      this.bindGuideRegionSelects();
    }
  }

  disconnectedCallback() {
    if (!this.initialized) return;
    this.teardownRegionSelects();
    this.teardownSubtabs();
    document.removeEventListener('size-guide:category-change', this.handleCategoryChange);
    this.initialized = false;
  }

  /**
   * @param {string} value
   * @returns {string}
   */
  normaliseCategory(value) {
    return String(value || '').trim().toLowerCase();
  }

  /**
   * @param {string} category
   * @returns {boolean}
   */
  hasGuideForCategory(category) {
    const key = this.normaliseCategory(category);
    if (!key) return false;
    return this.guides.some(
      (guide) => this.normaliseCategory(guide.dataset.guideKey) === key
    );
  }

  /**
   * Keeps data-active-category on the custom element in sync with the carousel card.
   * @param {string} category
   */
  setActiveCategory(category) {
    this.activeCategory = this.normaliseCategory(category);
    if (this.activeCategory) {
      this.dataset.activeCategory = this.activeCategory;
    } else {
      delete this.dataset.activeCategory;
    }
  }

  /** @param {CustomEvent} event */
  handleCategoryChange(event) {
    const { category } = event.detail || {};
    if (!category) return;

    const normalised = this.normaliseCategory(category);
    if (normalised === this.activeCategory) return;

    this.setActiveCategory(normalised);

    if (this.isDesignMode) return;

    this.applyCategoryVisibility();
  }

  /**
   * @param {HTMLElement} guide
   * @returns {string[]}
   */
  getGuideRegions(guide) {
    const fromAttr = (guide.dataset.guideRegions || '')
      .split(',')
      .map((r) => this.normaliseRegion(r))
      .filter(Boolean);

    const fromWrappers = [...guide.querySelectorAll('[data-guide-region]')].map((wrapper) =>
      this.normaliseRegion(wrapper.dataset.guideRegion)
    );

    return [...new Set([...fromAttr, ...fromWrappers].filter(Boolean))];
  }

  /**
   * Injects a region <select> when multiple regional tables exist but Liquid did not render one.
   */
  ensureRegionSelectors() {
    this.guides.forEach((guide) => {
      if (guide.querySelector('[data-guide-region-select]')) return;

      const regions = this.getGuideRegions(guide);
      if (regions.length < 2) return;

      const main = guide.querySelector('.size-guide__main');
      if (!main) return;

      const controls = document.createElement('div');
      controls.className = 'size-guide__controls';

      const selectId = `SizeRegionSelect-${this.dataset.sectionId || 'cst'}-${guide.dataset.guideKey || 'guide'}`.replace(
        /[^a-zA-Z0-9_-]/g,
        '-'
      );

      const label = document.createElement('label');
      label.className = 'custom-size-table__label visually-hidden';
      label.htmlFor = selectId;
      label.textContent = 'Region';

      const select = document.createElement('select');
      select.id = selectId;
      select.className = 'custom-size-table__select';
      select.setAttribute('data-guide-region-select', '');

      const defaultRegion = this.getGuideDefaultRegion(guide) || regions[0];
      regions.forEach((region) => {
        const option = document.createElement('option');
        option.value = region;
        option.textContent = region;
        if (region === defaultRegion) {
          option.selected = true;
        }
        select.appendChild(option);
      });

      controls.append(label, select);

      const firstTable = main.querySelector('[data-guide-region]');
      if (firstTable) {
        main.insertBefore(controls, firstTable);
      } else {
        main.prepend(controls);
      }
    });
  }

  bindGuideRegionSelects() {
    this.teardownRegionSelects();

    this.guides.forEach((guide) => {
      const select = guide.querySelector('[data-guide-region-select]');
      if (!select) return;

      const onChange = () => {
        this.showGuideRegion(guide, select.value);
      };

      select.addEventListener('change', onChange);
      this.regionSelectCleanups.push(() => select.removeEventListener('change', onChange));
    });
  }

  teardownRegionSelects() {
    this.regionSelectCleanups.forEach((fn) => fn());
    this.regionSelectCleanups = [];
  }

  /**
   * @param {string} region
   * @returns {string}
   */
  normaliseRegion(region) {
    return String(region || '').trim().toUpperCase();
  }

  /**
   * @param {HTMLElement} guide
   * @returns {string}
   */
  getGuideDefaultRegion(guide) {
    const select = guide.querySelector('[data-guide-region-select]');
    if (select?.value) {
      return this.normaliseRegion(select.value);
    }

    const fromAttr = (guide.dataset.guideRegions || '').split(',')[0];
    if (fromAttr) {
      return this.normaliseRegion(fromAttr);
    }

    const firstWrapper = guide.querySelector('[data-guide-region]');
    return this.normaliseRegion(firstWrapper?.dataset.guideRegion);
  }

  /**
   * @param {HTMLElement} guide
   * @param {string} region
   */
  showGuideRegion(guide, region) {
    const target = this.normaliseRegion(region);
    const wrappers = [...guide.querySelectorAll('[data-guide-region]')];

    if (!wrappers.length) {
      return;
    }

    if (!target) {
      wrappers.forEach((tw, index) => {
        tw.hidden = index !== 0;
      });
      return;
    }

    let anyVisible = false;
    wrappers.forEach((tw) => {
      const match = this.normaliseRegion(tw.dataset.guideRegion) === target;
      tw.hidden = !match;
      if (match) {
        anyVisible = true;
      }
    });

    if (!anyVisible) {
      wrappers.forEach((tw, index) => {
        tw.hidden = index !== 0;
      });
    }
  }

  showAllGuides() {
    this.guides.forEach((guide) => {
      guide.hidden = false;
      guide.classList.remove('size-guide--inactive');
    });
    this.ensureRegionSelectors();
    this.guides.forEach((guide) => {
      this.showGuideRegion(guide, this.getGuideDefaultRegion(guide));
    });
    this.bindGuideRegionSelects();
  }

  applyCategoryVisibility() {
    const filterByCategory =
      this.activeCategory && this.hasGuideForCategory(this.activeCategory);

    this.guides.forEach((guide) => {
      const isActive =
        !filterByCategory ||
        this.normaliseCategory(guide.dataset.guideKey) === this.activeCategory;
      guide.hidden = !isActive;
      guide.classList.toggle('size-guide--inactive', !isActive);
    });

    this.ensureRegionSelectors();

    const visibleGuides = this.guides.filter((guide) => !guide.hidden);
    visibleGuides.forEach((guide) => {
      this.showGuideRegion(guide, this.getGuideDefaultRegion(guide));
    });

    this.bindGuideRegionSelects();
  }

  /* ---------- Inline sub-tabs (multi_mode: tabs) ---------- */

  /**
   * Binds the inline sub-tabs (e.g. Header 1 / 2 / 3) rendered by Liquid.
   * Switching is local: it never dispatches size-guide:category-change, so the
   * external carousel and sibling tables are not affected.
   */
  setupSubtabs() {
    // One nav is rendered per guide (so the image can span beside it on desktop),
    // so we collect every button and delegate from the custom element root.
    this.subtabs = [...this.querySelectorAll('[data-sg-subtab]')];
    if (!this.subtabs.length) return;

    this.addEventListener('click', this.handleSubtabClick);
    this.addEventListener('keydown', this.handleSubtabKeydown);
    this.subtabsBound = true;
  }

  teardownSubtabs() {
    if (!this.subtabsBound) return;
    this.removeEventListener('click', this.handleSubtabClick);
    this.removeEventListener('keydown', this.handleSubtabKeydown);
    this.subtabsBound = false;
    this.subtabs = [];
  }

  /** @param {MouseEvent} event */
  handleSubtabClick(event) {
    const button = event.target.closest('[data-sg-subtab]');
    if (!button || !this.contains(button)) return;
    this.selectSubtab(button.dataset.sgSubtab || '');
  }

  /** @param {KeyboardEvent} event */
  handleSubtabKeydown(event) {
    const button = event.target.closest('[data-sg-subtab]');
    if (!button || !this.contains(button)) return;

    const { key } = event;
    if (key !== 'ArrowLeft' && key !== 'ArrowRight' && key !== 'Home' && key !== 'End') {
      return;
    }

    event.preventDefault();
    // Navigate within the nav that holds the focused button.
    const list = button.closest('.custom-size-table__subtabs-list');
    const navButtons = list ? [...list.querySelectorAll('[data-sg-subtab]')] : this.subtabs;
    const idx = navButtons.indexOf(button);
    if (idx === -1) return;

    let next = idx;
    if (key === 'ArrowLeft') {
      next = idx > 0 ? idx - 1 : navButtons.length - 1;
    } else if (key === 'ArrowRight') {
      next = idx < navButtons.length - 1 ? idx + 1 : 0;
    } else if (key === 'Home') {
      next = 0;
    } else if (key === 'End') {
      next = navButtons.length - 1;
    }

    const target = navButtons[next];
    if (target) {
      this.selectSubtab(target.dataset.sgSubtab || '', { focus: true });
    }
  }

  /**
   * Shows the guide matching the given sync key and updates sub-tab state.
   * @param {string} key
   * @param {{ focus?: boolean }} [options]
   */
  selectSubtab(key, options = {}) {
    const normalised = this.normaliseCategory(key);
    if (!normalised) return;

    this.setActiveCategory(normalised);
    this.applyCategoryVisibility();

    this.subtabs.forEach((tab) => {
      const isSel = this.normaliseCategory(tab.dataset.sgSubtab) === normalised;
      tab.setAttribute('aria-selected', isSel ? 'true' : 'false');
      tab.tabIndex = isSel ? 0 : -1;
    });

    if (options.focus) {
      // After switching, the matching button lives in the now-visible guide's nav.
      const activeGuide = this.guides.find((guide) => !guide.hidden);
      const button = activeGuide?.querySelector('[data-sg-subtab][aria-selected="true"]');
      button?.focus();
    }
  }

  /** @param {string} s */
  esc(s) {
    return String(s || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }
}


  customElements.define('custom-size-table', CustomSizeTable);
}

if (!customElements.get('unit-conversion')) {
    class UnitConversion extends HTMLElement {
        connectedCallback() {
            this.addEventListener('click', this.onClick.bind(this));
        }

        onClick(e) {
            const unit = e.target.value;
            if (unit && unit !== this.unit) {
				this.unit = unit;
				this.convertToUnit(unit);
				this.querySelector('button.active')?.classList.remove('active');
				e.target.classList.add('active');
			}
        }

        convertToUnit(unit = 'inch') {
            const allCells = this.closest('.size-guide__main')
                .querySelectorAll('table tbody td');

            const ONE_INCH_IN_CM = 2.54;
            // Matches ranges like "48-54", "48 - 54", "48 -54" (hyphen or en-dash)
            const rangePattern = /^(\d+(?:\.\d+)?)\s*[-\u2013]\s*(\d+(?:\.\d+)?)$/;
            // Matches the compact stored form "18.897...-21.259..." (no surrounding spaces)
            const storedRangePattern = /^(\d+(?:\.\d+)?)-(\d+(?:\.\d+)?)$/;

            allCells.forEach(cell => {
                const cellContent = cell.getAttribute('data-value') || cell.textContent.trim();

                const preciseValues = cellContent.split('/').map(rawValue => {
                    const trimmedValue = rawValue.trim();
                    const parsedValue = Number(trimmedValue);

                    if (!trimmedValue || !Number.isFinite(parsedValue)) {
                        const rangeMatch = trimmedValue.match(rangePattern);
                        if (rangeMatch) {
                            const a = unit === 'inch'
                                ? Number(rangeMatch[1]) / ONE_INCH_IN_CM
                                : Number(rangeMatch[1]) * ONE_INCH_IN_CM;
                            const b = unit === 'inch'
                                ? Number(rangeMatch[2]) / ONE_INCH_IN_CM
                                : Number(rangeMatch[2]) * ONE_INCH_IN_CM;
                            return `${a}-${b}`;
                        }
                        return trimmedValue;
                    }

                    return unit === 'inch'
                        ? parsedValue / ONE_INCH_IN_CM
                        : parsedValue * ONE_INCH_IN_CM;
                });

                const roundedValues = preciseValues.map(value => {
                    if (typeof value !== 'number') {
                        const rangeMatch = String(value).match(storedRangePattern);
                        if (rangeMatch) {
                            const ra = String(Math.round(Number(rangeMatch[1]) * 10) / 10);
                            const rb = String(Math.round(Number(rangeMatch[2]) * 10) / 10);
                            return `${ra} - ${rb}`;
                        }
                        return value;
                    }

                    return String(Math.round(value * 10) / 10);
                });

                cell.setAttribute('data-value', preciseValues.join('/'));
                cell.setAttribute('data-unit', unit);
                cell.textContent = roundedValues.join(' / ');
            });
        }
    }

    customElements.define('unit-conversion', UnitConversion);
}